import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { useLanguage } from '../../contexts/LanguageContext';

function ActionOutcomePanel({
  outcome,
  onDismiss,
  onUndo,
  className = '',
  autoDismissMs = 12000,
}) {
  const { t } = useLanguage();
  const copy = t.ux?.actionOutcome;

  useEffect(() => {
    if (!outcome || !autoDismissMs || onUndo) return undefined;
    const timer = window.setTimeout(() => onDismiss?.(), autoDismissMs);
    return () => window.clearTimeout(timer);
  }, [outcome, autoDismissMs, onDismiss, onUndo]);

  return (
    <AnimatePresence>
      {outcome ? (
        <motion.div
          key={outcome.id || outcome.type}
          role="status"
          initial={{ opacity: 0, y: -8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -8, height: 0 }}
          transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className={`overflow-hidden ${className}`}
        >
          <div className="card-premium border border-success-200/80 bg-success-50/90 p-4   sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-success-500 text-white shadow-soft">
                <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-success-900 ">{outcome.title}</p>
                {outcome.description ? (
                  <p className="mt-1 text-sm text-success-800/90 ">{outcome.description}</p>
                ) : null}
                {outcome.nextStep ? (
                  <p className="mt-2 text-sm font-medium text-success-700 ">
                    <span className="font-semibold">{copy?.nextStepLabel || 'Next:'}</span> {outcome.nextStep}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  {onUndo ? (
                    <Button type="button" variant="outline" size="compact" onClick={onUndo}>
                      {copy?.undo || 'Undo'}
                    </Button>
                  ) : null}
                  <Button type="button" variant="secondary" size="compact" onClick={onDismiss}>
                    {copy?.dismiss || 'Got it'}
                  </Button>
                </div>
              </div>
              <button
                type="button"
                onClick={onDismiss}
                className="touch-target shrink-0 rounded-xl p-1 text-success-600 transition hover:bg-success-100  "
                aria-label={copy?.dismiss || 'Dismiss'}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default ActionOutcomePanel;
